import { CommonModule } from '@angular/common';
import { Component, DestroyRef, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AbstractControl, FormGroup } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { TRANSLOCO_SCOPE, TranslocoModule } from '@jsverse/transloco';
import { QaiToggleSwitchComponent } from '@qai/ui';
import { DetailsSectionComponent } from 'src/app/pages/self-serve/simulation-builder/components/details-container/details-section.component';
import { AuthorReactiveFormService } from 'src/app/pages/self-serve/simulation-builder/services/author-reactive-form.service';
import { AmplitudeService } from 'src/app/services/external-tools/amplitude.service';
import { AuthorService } from 'src/app/services/self-serve/author.service';
import { SimulationConstants } from 'src/app/utils/constants/self-serve/simulation.constants';
import { AmplitudeTrackingEvents } from 'src/app/utils/enums/amplitude/amplitudeTrackingEvents.enum';

/**
 * Sim-level Accuracy Feedback toggle.
 *
 * State lives in the shared simulation reactive form at
 * `scoring.accuracyFeedback`, alongside the other scoring sections. The form
 * control is the source of truth; `checked` mirrors it for the template.
 *
 * The toggle is only editable when the sim has at least one linked accuracy
 * document (`documentCount > 0`) and the sim is not published; with no corpus
 * there is nothing to score statements against, and the backend rejects a
 * changed value for a PUBLISHED call, so we prevent that client-side.
 *
 * A stored `enabled: true` with no documents renders off and disabled rather
 * than on, since accuracy cannot run without a corpus.
 */
@Component({
  selector: 'accuracy-feedback-section',
  standalone: true,
  templateUrl: './accuracy-feedback-section.component.html',
  styleUrls: ['./accuracy-feedback-section.component.scss'],
  imports: [
    CommonModule,
    TranslocoModule,
    DetailsSectionComponent,
    QaiToggleSwitchComponent,
  ],
  providers: [{ provide: TRANSLOCO_SCOPE, useValue: 'simulationBuilder' }],
})
export class AccuracyFeedbackSectionComponent {
  accuracyFeedbackGroup!: FormGroup;

  /** The `:uuid` route param — also the form store's key and the sim uuid. */
  simulationUuid: string = '';
  moduleUuid: string = '';

  checked: boolean = false;
  documentCount: number = 0;
  readOnly: boolean = false;

  private readonly route = inject(ActivatedRoute);
  private readonly authorService = inject(AuthorService);
  private readonly authorReactiveFormService = inject(AuthorReactiveFormService);
  private readonly amplitudeService = inject(AmplitudeService);
  private readonly destroyRef = inject(DestroyRef);

  ngOnInit(): void {
    this.simulationUuid = this.route.snapshot.paramMap.get('uuid') ?? '';
    if (!this.simulationUuid) return;

    this.readOnly = this.authorReactiveFormService.isReadOnly(
      this.simulationUuid
    );

    this.authorReactiveFormService
      .getForm(this.simulationUuid)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((form) => {
        this.moduleUuid =
          form?.get(SimulationConstants.MODULE_UUID_PATH)?.value ?? '';
        this.accuracyFeedbackGroup = form?.get(
          SimulationConstants.ACCURACY_FEEDBACK_PATH
        ) as FormGroup;

        this.checked = !!this.enabledControl?.value;
        this.documentCount = this.documentCountControl?.value ?? 0;

        this.authorReactiveFormService.setReadOnlyMode(
          this.simulationUuid,
          this.enabledControl
        );
      });
  }

  get hasDocuments(): boolean {
    return this.documentCount > 0;
  }

  get toggleDisabled(): boolean {
    return this.readOnly || !this.hasDocuments;
  }

  /**
   * With no corpus the sim scores nothing, so the toggle reads off whatever
   * the stored flag says. Display only — the stored value is left untouched.
   */
  get toggleOn(): boolean {
    return this.checked && this.hasDocuments;
  }

  get statusLabelKey(): string {
    if (!this.hasDocuments) {
      return 'ACCURACY_FEEDBACK_STATUS_DISABLED';
    }
    return this.checked
      ? 'ACCURACY_FEEDBACK_STATUS_ON'
      : 'ACCURACY_FEEDBACK_STATUS_OFF';
  }

  get descriptionKey(): string {
    return this.hasDocuments
      ? 'ACCURACY_FEEDBACK_DESCRIPTION'
      : 'ACCURACY_FEEDBACK_NO_DOCUMENTS';
  }

  onToggle(event: Event): void {
    if (this.toggleDisabled) return;

    const next = (event.target as HTMLInputElement).checked;
    const previous = this.checked;

    this.setEnabled(next);

    this.amplitudeService.trackEvent(AmplitudeTrackingEvents.SCORING_ACCURACY, {
      sim_id: this.simulationUuid,
      enabled: next,
    });

    this.authorService
      .updateSimulation({
        uuid: this.simulationUuid,
        existingModuleUuid: this.moduleUuid,
        accuracyFeedbackEnabled: next,
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        error: () => this.setEnabled(previous),
      });
  }

  /**
   * `emitEvent: false` keeps the `(change)` handler the single save path —
   * there is no valueChanges autosave on this section, because a toggle is a
   * discrete action rather than free text needing a debounce.
   */
  private setEnabled(value: boolean): void {
    this.enabledControl?.setValue(value, { emitEvent: false });
    this.checked = value;
  }

  private get enabledControl(): AbstractControl | null {
    return this.accuracyFeedbackGroup?.get('enabled') ?? null;
  }

  private get documentCountControl(): AbstractControl | null {
    return this.accuracyFeedbackGroup?.get('documentCount') ?? null;
  }
}
